/**
 * 主布局组件
 * 作为应用的主要布局容器
 */

import React from 'react';
import { useAppViewModel } from '../../viewmodels/appviewmodel.jsx';
import { useTheme } from '../../design-system/hooks/useTheme.jsx';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Sidebar from '../components/Sidebar.jsx';
import NotificationContainer from '../notifications/NotificationContainer.jsx';
import LoadingOverlay from '../loading/LoadingOverlay.jsx';
import ErrorBoundary from '../errors/ErrorBoundary.jsx';

/**
 * 主布局组件
 * @param {Object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @param {boolean} props.showSidebar - 是否显示侧边栏
 * @param {boolean} props.showHeader - 是否显示头部
 * @param {boolean} props.showFooter - 是否显示底部
 * @param {string} props.className - 自定义样式类
 */
function MainLayout({ 
  children, 
  showSidebar = true, 
  showHeader = true, 
  showFooter = true,
  className = '' 
}) {
  const { 
    isLoading, 
    globalState, 
    isInitialized,
    closeModal
  } = useAppViewModel();
  
  const { currentTheme } = useTheme();

  // 如果应用未初始化，显示加载状态
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingOverlay 
          isVisible={true} 
          message="正在初始化应用..."
        />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div 
        className={`
          min-h-screen 
          bg-background 
          text-foreground 
          transition-colors 
          duration-300
          ${currentTheme?.name || 'light'}
          ${className}
        `}
        data-theme={currentTheme?.name}
      >
        {/* 全局加载遮罩 */}
        <LoadingOverlay 
          isVisible={isLoading} 
          message="加载中..."
        />
        
        {/* 主要布局容器 */}
        <div className="flex min-h-screen">
          {/* 侧边栏 */}
          {showSidebar && (
            <aside className="w-64 bg-card border-r border-border hidden lg:block lg:flex-shrink-0">
              <Sidebar />
            </aside>
          )}
          
          {/* 主内容区域 */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* 头部 */}
            {showHeader && (
              <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
                <Header />
              </header>
            )}
            
            {/* 主内容 */}
            <main className="flex-1 overflow-auto">
              <div className="container mx-auto px-4 py-6">
                {children}
              </div>
            </main>
            
            {/* 底部 */}
            {showFooter && (
              <footer className="bg-card border-t border-border">
                <Footer />
              </footer>
            )}
          </div>
        </div>
        
        {/* 通知容器 */}
        <NotificationContainer />
        
        {/* 模态框容器 */}
        {globalState.modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50" />
            <div className="relative bg-card rounded-lg shadow-lg max-w-md w-full mx-4">
              {/* 模态框内容将在这里渲染 */}
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {globalState.modalType}
                </h3>
                <div className="text-sm text-muted-foreground">
                  {JSON.stringify(globalState.modalData)}
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  <button 
                    className="px-4 py-2 text-sm bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
                    onClick={() => {
                      closeModal();
                    }}
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default MainLayout;